import React, { useState, useEffect, useRef } from "react";
import {
  getSelectedCompanyProfiles,
  selectedCompanyHasProfile,
} from "../utils/activityProfiles";
import {
  Card,
  Typography,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Table,
  Alert,
  Spin,
  Space,
  message,
  Avatar,
  Descriptions,
  Modal,
  Empty,
  Tag,
  Tooltip,
  Collapse,
} from "antd";
import {
  ArrowLeftOutlined,
  CalculatorOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
  ReloadOutlined,
  EditOutlined,
  ShoppingCartOutlined,
  WarningFilled,
  RestOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getProductionUnitById,
  getProductionUnits,
  calculateProductionNeeds,
  processProduction,
  prefetchProductionOfflineBootstrap,
  cancelProduction,
  finalizeProduction,
  revalueProductionLot,
} from "./api";
import ProductionCapacityCalculator from "./ProductionCapacityCalculator";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { getProductionTerminology } from "./productionTerminology";
import {
  buildMissingStockItems,
  buildSalesProductionReplenishmentNavigation,
  buildStockReplenishmentRequest,
  buildStockReplenishmentReturnNavigation,
  getSalesProductionReplenishmentStep,
  isSalesProductionReplenishment,
} from "../utils/stockReplenishment";
import StockShortageActionsModal from "../components/StockShortageActionsModal";
import { matchesProductionUnitSearch } from "./productionUnitSearch";
import {
  getLaunchDisabledReason,
  getLotWatchProductNames,
  getMaterialAttentionState,
  getMaterialMissingQuantity,
  sortMaterialsForDecision,
} from "./productionProcessPresentation";
import "./production-process-desktop.css";

const { Title, Text } = Typography;
const { Option } = Select;

const PRODUCTION_KIND_SEMI_FINISHED = "semi_finished";
const RESTAURANT_CUISINE_CONVERT_PERMISSION =
  "Gestion Commerciale.Restauration.Cuisine.convert_to_production";
const PRODUCTION_MANAGE_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.create";
const PURCHASE_VIEW_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Achats.Achat.view";
const PURCHASE_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Achats.Achat.create";
const STOCK_ADJUST_VIEW_PERMISSION =
  "Gestion Commerciale.Stock.GestionStock.Adjust.view";
const STOCK_ADJUST_CREATE_PERMISSION =
  "Gestion Commerciale.Stock.GestionStock.Adjust.create";
const MAX_PRODUCTION_OUTPUT_QUANTITY = 999999.99;
const getProductionKindLabel = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "Semi-fini" : "Fini";

const toPositiveInt = (value) => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const toPositiveNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const buildRestaurantTicketContext = (location) => {
  const params = new URLSearchParams(location.search);
  const stateTicket = location.state?.restaurantTicket || null;
  const ticketId =
    toPositiveInt(params.get("restaurant_ticket_id")) ||
    toPositiveInt(stateTicket?.id);

  if (!ticketId) {
    return null;
  }

  const requestedQuantity =
    toPositiveNumber(params.get("requested_quantity")) ||
    toPositiveNumber(stateTicket?.requested_quantity) ||
    toPositiveNumber(stateTicket?.line_quantity);
  const eligibleQuantity =
    toPositiveNumber(params.get("eligible_quantity")) ||
    toPositiveNumber(stateTicket?.eligible_quantity) ||
    requestedQuantity;
  const outputQuantity =
    toPositiveNumber(params.get("output_quantity")) ||
    eligibleQuantity ||
    requestedQuantity ||
    1;

  return {
    ...stateTicket,
    id: ticketId,
    production_unit_id:
      toPositiveInt(params.get("production_unit_id")) ||
      toPositiveInt(stateTicket?.production_unit_id),
    output_quantity: outputQuantity,
    requested_quantity: requestedQuantity,
    eligible_quantity: eligibleQuantity,
    status: params.get("status") || stateTicket?.status || null,
  };
};

// Contexte de "régularisation de fin de journée" : la production lancée depuis
// l'écran Régularisation pré-remplit l'unité et la quantité (= déficit vendu
// sans production) et marque la production comme régularisation.
const buildRegularizationContext = (location) => {
  const params = new URLSearchParams(location.search);
  const isRegularization =
    params.get("regularization") === "1" ||
    params.get("regularization") === "true";
  if (!isRegularization) {
    return null;
  }
  const productionUnitId = toPositiveInt(params.get("production_unit_id"));
  if (!productionUnitId) {
    return null;
  }
  return {
    production_unit_id: productionUnitId,
    output_quantity: toPositiveNumber(params.get("output_quantity")) || 1,
  };
};

// Helper function for currency formatting
const formatCurrency = (value) => {
  if (value === undefined || value === null || isNaN(parseFloat(value))) {
    return "N/A";
  }
  const numValue = parseFloat(value);
  const roundedValue = Math.round(numValue);
  // 'fr-FR' typically uses a space as a thousand separator and no decimals for whole numbers.
  return `${roundedValue.toLocaleString("fr-FR")} CFA`;
};

const formatPercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "0 %";
  }

  return `${Math.round(numericValue * 100)} %`;
};

const formatQuantity = (
  value,
  { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}
) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return numericValue.toLocaleString("fr-FR", {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

const formatSignedCurrency = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "0 CFA";
  }

  const sign = numericValue > 0 ? "+" : numericValue < 0 ? "−" : "";
  return `${sign}${Math.abs(Math.round(numericValue)).toLocaleString("fr-FR")} CFA`;
};

const lowercaseFirstLetter = (value) => {
  const text = String(value || "").trim();
  if (!text) {
    return "ingrédient";
  }

  return `${text.charAt(0).toLocaleLowerCase("fr")}${text.slice(1)}`;
};

const formatDisplayDate = (value) => {
  if (!value) {
    return "date non renseignee";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "date non renseignee";
  }

  return parsedDate.toLocaleDateString("fr-FR");
};

const getLotSourceLabel = (allocation) => {
  const sourceType = String(allocation?.source_type || "").toLowerCase();
  const lotCode = String(allocation?.lot_code || "").toUpperCase();

  if (sourceType === "purchase_order_item" || lotCode.startsWith("PUR-")) {
    return "Stock issu de l'achat";
  }

  if (sourceType === "production_log") {
    return "Stock issu de la production";
  }

  if (lotCode.startsWith("LEGACY-")) {
    return "Stock historique cree";
  }

  return "Stock issu du lot";
};

const renderLotAllocationTooltip = (allocation, unitShortName) => {
  const lotCode = allocation?.lot_code || allocation?.lotId || allocation?.lot_id;
  const quantity = Number(allocation?.quantity_allocated ?? allocation?.quantity ?? 0);
  const renderedQuantity = Number.isFinite(quantity) ? quantity.toFixed(2) : "0.00";
  const unitCost = Number(allocation?.unit_cost);

  return (
    <div>
      <div>
        {getLotSourceLabel(allocation)} du {formatDisplayDate(allocation?.produced_at)}
      </div>
      <div>
        Cout unitaire utilise:{" "}
        {Number.isFinite(unitCost) ? formatCurrency(unitCost) : "N/A"}
        {unitShortName ? ` / ${unitShortName}` : ""}
      </div>
      <div>
        Quantite consommee: {renderedQuantity}
        {unitShortName ? ` ${unitShortName}` : ""}
      </div>
      {lotCode && <div>Reference lot: {lotCode}</div>}
    </div>
  );
};

const renderMessageList = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {items.map((item, index) => (
        <li key={`${item?.code || "item"}-${index}`}>
          {item?.message || item}
        </li>
      ))}
    </ul>
  );
};

const renderGuardrailDescription = (guardrails) => {
  const guidance = guardrails?.user_guidance;

  if (!guidance) {
    const fallbackItems =
      guardrails?.status === "blocked"
        ? guardrails?.blocking_reasons
        : guardrails?.warning_reasons;
    return renderMessageList(fallbackItems);
  }

  return (
    <div>
      {guidance.summary && (
        <p style={{ marginBottom: 12 }}>{guidance.summary}</p>
      )}

      {Array.isArray(guidance.reasons) && guidance.reasons.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text strong>Pourquoi ?</Text>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {guidance.reasons.map((reason, index) => (
              <li key={`reason-${index}`}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(guidance.actions) && guidance.actions.length > 0 && (
        <div style={{ marginBottom: guidance.main_driver ? 12 : 0 }}>
          <Text strong>Que faire maintenant ?</Text>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {guidance.actions.map((action, index) => (
              <li key={action?.code || `action-${index}`}>
                <span style={{ fontWeight: 600 }}>
                  {action?.title || "Action"}
                </span>
                {action?.description ? `: ${action.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {guidance.main_driver && (
        <Text type="secondary">{guidance.main_driver}</Text>
      )}
    </div>
  );
};

const renderLotWatchList = (guardrails) => {
  const watchItems = guardrails?.user_guidance?.lot_watch_items;

  if (!Array.isArray(watchItems) || watchItems.length === 0) {
    return null;
  }

  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {watchItems.map((item, index) => (
        <li key={item?.code || `watch-${index}`}>
          <span style={{ fontWeight: 600 }}>{item?.title || "Stock a verifier"}</span>
          {item?.detail ? `: ${item.detail}` : ""}
        </li>
      ))}
    </ul>
  );
};

const getDefaultOutputQuantity = (unitData, unitId) => {
  if (!unitData || !unitData.outputs || unitData.outputs.length === 0) {
    return 1;
  }

  const normalizedUnitId = Number(unitId);
  const selfOutput = unitData.outputs.find(
    (output) => Number(output.product_id) === normalizedUnitId
  );
  const quantity = selfOutput?.quantity ?? unitData.outputs[0].quantity;
  const parsedQuantity = Number(quantity);

  return !Number.isNaN(parsedQuantity) && parsedQuantity > 0
    ? parsedQuantity
    : 1;
};

const ProductionProcess = ({ restaurantMode = false }) => {
  const [form] = Form.useForm();
  const [lotResolutionForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const productionTerminology = getProductionTerminology(
    getSelectedCompanyProfiles(user, selectedCompany)
  );

  const restaurantTicketContext = React.useMemo(
    () => buildRestaurantTicketContext(location),
    [location]
  );
  const regularizationContext = React.useMemo(
    () => buildRegularizationContext(location),
    [location]
  );
  const stockReplenishmentReturnContext = React.useMemo(() => {
    const context = location.state?.stockReplenishmentReturn;
    const productionUnitId = toPositiveInt(context?.production_unit_id);
    if (!context || !productionUnitId) return null;

    return {
      ...context,
      production_unit_id: productionUnitId,
      output_quantity: toPositiveNumber(context.output_quantity),
    };
  }, [location.state]);
  const salesProductionReplenishmentContext = React.useMemo(() => {
    const context = location.state?.salesProductionReplenishment;
    if (!isSalesProductionReplenishment(context)) return null;

    const selectedCompanyId = Number(selectedCompany?.id ?? selectedCompany);
    const selectedWarehouseId = Number(
      selectedWarehouse?.id ?? selectedWarehouse
    );
    const scopeMatches =
      Number(context.user_id) === Number(user?.id) &&
      Number(context.company_id) === selectedCompanyId &&
      Number(context.warehouse_id) === selectedWarehouseId;
    return scopeMatches ? context : null;
  }, [
    location.state,
    selectedCompany,
    selectedWarehouse,
    user?.id,
  ]);
  const salesProductionStep = React.useMemo(
    () =>
      getSalesProductionReplenishmentStep(
        salesProductionReplenishmentContext
      ),
    [salesProductionReplenishmentContext]
  );
  const clearStockReplenishmentReturn = () => {
    if (!location.state?.stockReplenishmentReturn) return;
    const nextState = { ...(location.state || {}) };
    delete nextState.stockReplenishmentReturn;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null,
      }
    );
  };
  const isRestaurantActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "restaurant"
  );

  // Ref pour tracker le magasin précédent
  const prevWarehouseRef = useRef(selectedWarehouse);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [calculationResults, setCalculationResults] = useState(null);
  const [producing, setProducing] = useState(false);

  // New state for the production result modal
  const [productionResultModalVisible, setProductionResultModalVisible] =
    useState(false);
  const [productionResultData, setProductionResultData] = useState(null);
  const [lotResolutionModalVisible, setLotResolutionModalVisible] =
    useState(false);
  const [lotResolutionSubmitting, setLotResolutionSubmitting] =
    useState(false);
  const [selectedQuickAction, setSelectedQuickAction] = useState(null);
  const [stockShortageActionsVisible, setStockShortageActionsVisible] =
    useState(false);
  const [capacityCalculatorVisible, setCapacityCalculatorVisible] =
    useState(false);
  const [advancedDetailsOpen, setAdvancedDetailsOpen] = useState(false);
  const displayedRestaurantTicket =
    calculationResults?.restaurant_ticket && restaurantTicketContext
      ? {
          ...restaurantTicketContext,
          ...calculationResults.restaurant_ticket,
        }
      : restaurantTicketContext;
  const canLaunchProduction =
    hasPermission(PRODUCTION_MANAGE_CREATE_PERMISSION) ||
    (restaurantMode &&
      hasPermission(RESTAURANT_CUISINE_CONVERT_PERMISSION));
  const canCreatePurchase =
    hasPermission(PURCHASE_VIEW_PERMISSION) &&
    hasPermission(PURCHASE_CREATE_PERMISSION);
  const canCreateStockAdjustment =
    hasPermission(STOCK_ADJUST_VIEW_PERMISSION) &&
    hasPermission(STOCK_ADJUST_CREATE_PERMISSION);

  // Obtenir le nom du magasin sélectionné
  const selectedWarehouseName = React.useMemo(() => {
    if (!selectedWarehouse || !user?.assigned_warehouses) return null;
    const warehouse = user.assigned_warehouses.find(
      (w) => w.id === selectedWarehouse
    );
    return warehouse ? warehouse.name : `Établissement ID: ${selectedWarehouse}`;
  }, [selectedWarehouse, user]);
  const clearRestaurantTicketSelection = React.useCallback(() => {
    if (!restaurantMode) {
      return;
    }

    navigate("/production/kitchen/process", { replace: true });
  }, [navigate, restaurantMode]);

  useEffect(() => {
    if (!selectedWarehouse || !selectedCompany) {
      return;
    }

    prefetchProductionOfflineBootstrap({
      warehouse_id: selectedWarehouse,
      company_id: selectedCompany,
    }).catch((error) => {
      console.error(
        "Unable to prefetch production offline bootstrap:",
        error
      );
    });
  }, [selectedWarehouse, selectedCompany]);

  // Charger les unités de production et en sélectionner une si id est fourni
  useEffect(() => {
    const routeUnitId =
      salesProductionStep?.item?.production_unit_id ||
      stockReplenishmentReturnContext?.production_unit_id ||
      restaurantTicketContext?.production_unit_id ||
      regularizationContext?.production_unit_id ||
      (id ? parseInt(id, 10) : null);
    const routeOutputQuantity =
      salesProductionStep?.item?.output_quantity ||
      stockReplenishmentReturnContext?.output_quantity ||
      restaurantTicketContext?.output_quantity ||
      regularizationContext?.output_quantity ||
      null;

    loadUnits();
    if (routeUnitId && selectedWarehouse) {
      form.setFieldsValue({
        production_unit_id: routeUnitId,
        output_quantity: routeOutputQuantity || undefined,
      });
      handleUnitChange(routeUnitId, {
        outputQuantityOverride: routeOutputQuantity,
        restaurantTicketId: restaurantTicketContext?.id,
      });
    }
  }, [
    id,
    selectedWarehouse,
    selectedCompany,
    restaurantTicketContext?.id,
    restaurantTicketContext?.production_unit_id,
    restaurantTicketContext?.output_quantity,
    regularizationContext?.production_unit_id,
    regularizationContext?.output_quantity,
    stockReplenishmentReturnContext?.production_unit_id,
    stockReplenishmentReturnContext?.output_quantity,
    salesProductionStep?.item?.production_unit_id,
    salesProductionStep?.item?.output_quantity,
  ]);

  // Nouveau useEffect pour détecter les changements de magasin et réinitialiser le formulaire
  useEffect(() => {
    // Vérifier si c'est un vrai changement de magasin (pas le premier chargement)
    if (
      prevWarehouseRef.current !== null &&
      prevWarehouseRef.current !== selectedWarehouse
    ) {
      // Réinitialiser tous les états
      form.resetFields();
      setSelectedUnit(null);
      setCalculationResults(null);
      setAdvancedDetailsOpen(false);
      setProductionResultData(null);
      setProductionResultModalVisible(false);

      // Afficher un message informatif
      if (selectedWarehouse) {
        message.info("Établissement changé - Formulaire réinitialisé");
      }

      // Si on a un ID dans l'URL et que le magasin a changé,
      // on va recharger l'unité pour vérifier qu'elle appartient au nouveau magasin
      const nextUnitId =
        salesProductionStep?.item?.production_unit_id ||
        stockReplenishmentReturnContext?.production_unit_id ||
        restaurantTicketContext?.production_unit_id ||
        (id ? parseInt(id, 10) : null);
      if (nextUnitId && selectedWarehouse) {
        form.setFieldsValue({
          production_unit_id: nextUnitId,
          output_quantity:
            salesProductionStep?.item?.output_quantity ||
            stockReplenishmentReturnContext?.output_quantity ||
            restaurantTicketContext?.output_quantity ||
            undefined,
        });
        handleUnitChange(nextUnitId, {
          outputQuantityOverride:
            salesProductionStep?.item?.output_quantity ||
            stockReplenishmentReturnContext?.output_quantity ||
            restaurantTicketContext?.output_quantity,
          restaurantTicketId: restaurantTicketContext?.id,
        });
      }
    }

    // Mettre à jour la référence pour le prochain changement
    prevWarehouseRef.current = selectedWarehouse;
  }, [
    selectedWarehouse,
    id,
    form,
    restaurantTicketContext?.id,
    restaurantTicketContext?.production_unit_id,
    restaurantTicketContext?.output_quantity,
    stockReplenishmentReturnContext?.production_unit_id,
    stockReplenishmentReturnContext?.output_quantity,
    salesProductionStep?.item?.production_unit_id,
    salesProductionStep?.item?.output_quantity,
  ]);

  // Charger toutes les unités de production filtrées par magasin
  const loadUnits = async () => {
    if (!selectedWarehouse) {
      setUnits([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getProductionUnits({
        page: 1,
        limit: 100,
        warehouse_id: selectedWarehouse,
        company_id: selectedCompany,
      });

      // Filtrer côté client si l'API ne le fait pas déjà
      let filteredUnits = data.units || [];
      if (filteredUnits.length > 0) {
        filteredUnits = filteredUnits.filter(
          (unit) => unit.warehouse_id === selectedWarehouse
        );
      }

      setUnits(filteredUnits);
    } catch (error) {
      message.error(
        `Erreur lors du chargement des ${productionTerminology.unitPlural}`
      );
      console.error(error);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les détails d'une unité de production
  const handleUnitChange = async (unitId, options = {}) => {
    const {
      outputQuantityOverride = null,
      restaurantTicketId = restaurantTicketContext?.id || null,
    } = options;

    if (!unitId) {
      setSelectedUnit(null);
      setCalculationResults(null);
      setAdvancedDetailsOpen(false);
      return;
    }

    setAdvancedDetailsOpen(false);
    setLoading(true);
    try {
      const data = await getProductionUnitById(unitId, {
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
      });

      // Vérifier que l'unité appartient au magasin sélectionné
      if (data.warehouse_id !== selectedWarehouse) {
        message.error(
          `Cette ${productionTerminology.unitSingular} n'appartient pas à l'établissement sélectionné`
        );
        setSelectedUnit(null);
        form.setFieldsValue({ production_unit_id: undefined });
        return;
      }

      setSelectedUnit(data);
      const defaultOutputQuantity =
        outputQuantityOverride && Number(outputQuantityOverride) > 0
          ? Number(outputQuantityOverride)
          : getDefaultOutputQuantity(data, unitId);
      form.setFieldsValue({ output_quantity: defaultOutputQuantity });

      // Calculer les besoins pour la quantité par défaut
      handleCalculate(
        {
          production_unit_id: unitId,
          output_quantity: defaultOutputQuantity,
          restaurant_ticket_id: restaurantTicketId,
          __offline_unit_snapshot: data,
        },
        {
          silentError: true,
          silentOfflineFeedback: true,
        }
      );
    } catch (error) {
      message.error(
        `Erreur lors du chargement de ${productionTerminology.unitSingular}`
      );
      console.error(error);
      setSelectedUnit(null);
    } finally {
      setLoading(false);
    }
  };

  // Calculer les besoins en intrants
  const handleCalculate = async (values, options = {}) => {
    const { silentError = false, silentOfflineFeedback = false } = options;

    if (!selectedWarehouse) {
      message.warning(
        "Veuillez sélectionner un établissement avant de calculer les besoins"
      );
      return;
    }

    const payload = {
      ...values,
      restaurant_ticket_id:
        values.restaurant_ticket_id || restaurantTicketContext?.id || null,
      warehouse_id: selectedWarehouse,
      company_id: selectedCompany,
      __offline_unit_snapshot:
        values.__offline_unit_snapshot || selectedUnit || null,
    };

    setLoading(true);
    try {
      const data = await calculateProductionNeeds(payload);
      setCalculationResults(data);
      if (!silentOfflineFeedback && data?.local_estimate) {
        message.warning(
          data?.offline_warning ||
            "Calcul hors ligne reconstruit localement a partir du dernier cache disponible."
        );
      } else if (!silentOfflineFeedback && data?.offline) {
        message.info(
          "Calcul charge depuis le cache local. Il sera reverifie au retour de la connexion."
        );
      }
    } catch (error) {
      setCalculationResults(null);
      if (!silentError) {
        message.error(
          error?.error ||
            error?.message ||
            `Erreur lors du calcul des besoins en ${productionTerminology.materialPlural}`
        );
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCapacity = async (quantity) => {
    if (!selectedUnit?.id || !(Number(quantity) > 0)) {
      return;
    }

    const outputQuantity = Number(quantity);
    form.setFieldsValue({ output_quantity: outputQuantity });
    setCalculationResults(null);
    setCapacityCalculatorVisible(false);
    await handleCalculate({
      production_unit_id: selectedUnit.id,
      output_quantity: outputQuantity,
    });
  };

  // Invalidate calculation results if output quantity changes after a calculation has been made
  const handleFormValuesChange = (changedValues) => {
    if (changedValues.hasOwnProperty("output_quantity") && calculationResults) {
      setCalculationResults(null);
      setAdvancedDetailsOpen(false);
    }
  };

  const handleCloseLotResolutionModal = (force = false) => {
    if (lotResolutionSubmitting && !force) {
      return;
    }

    setLotResolutionModalVisible(false);
    setSelectedQuickAction(null);
    lotResolutionForm.resetFields();
  };

  const openLotResolutionModal = (action) => {
    if (!action?.lot_id) {
      return;
    }

    setSelectedQuickAction(action);
    lotResolutionForm.setFieldsValue({
      unit_cost:
        Number(action.purchase_price_reference) > 0
          ? Number(action.purchase_price_reference)
          : Number(action.current_unit_cost) || undefined,
      notes: "",
    });
    setLotResolutionModalVisible(true);
  };

  const handleSubmitLotResolution = async (values) => {
    if (!selectedQuickAction?.lot_id) {
      return;
    }

    setLotResolutionSubmitting(true);
    try {
      const result = await revalueProductionLot(selectedQuickAction.lot_id, {
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
        unit_cost: values.unit_cost,
        notes: values.notes,
      });

      message.success(
        result?.message || "Revalorisation du lot enregistree avec succes"
      );
      handleCloseLotResolutionModal(true);

      const currentValues = form.getFieldsValue([
        "production_unit_id",
        "output_quantity",
      ]);

      if (currentValues?.production_unit_id && currentValues?.output_quantity) {
        await handleCalculate(currentValues);
      } else {
        setCalculationResults(null);
      }
    } catch (error) {
      console.error("Erreur lors de la revalorisation du lot:", error);
      message.error(
        error?.error ||
          error?.message ||
          "Erreur lors de la revalorisation du lot"
      );
    } finally {
      setLotResolutionSubmitting(false);
    }
  };

  const handleGuardrailQuickAction = (action) => {
    if (!action?.type) {
      return;
    }

    if (action.type === "replenish_missing_stock") {
      const missingItems = buildMissingStockItems(
        calculationResults?.materials
      );

      if (missingItems.length === 0) {
        message.info(
          "Aucun stock manquant n'a été détecté. Relancez le calcul des besoins."
        );
        return;
      }

      if (!canCreatePurchase && !canCreateStockAdjustment) {
        message.error(
          "Vous n'avez pas la permission de créer un achat ou un ajustement de stock."
        );
        return;
      }

      setStockShortageActionsVisible(true);
      return;
    }

    if (action.type === "revalue_lot") {
      openLotResolutionModal(action);
      return;
    }

    if (action.type === "edit_recipe") {
      if (!selectedUnit?.id) {
        return;
      }
      navigate(`/production/units/edit/${selectedUnit.id}`);
      return;
    }

  };

  const canUseQuickAction = (action) => {
    if (!action?.type) {
      return false;
    }

    if (action.type === "replenish_missing_stock") {
      return canCreatePurchase || canCreateStockAdjustment;
    }

    if (action.type === "revalue_lot") {
      return (
        hasPermission(
          "Gestion Commerciale.Approvisionnement.Production.Gerer.create"
        ) ||
        hasPermission("Gestion Commerciale.Stock.GestionStock.Adjust.create")
      );
    }

    if (action.type === "edit_recipe") {
      return (
        Boolean(selectedUnit?.id) &&
        hasPermission(
          "Gestion Commerciale.Approvisionnement.Production.Unites.edit"
        )
      );
    }

    return false;
  };

  const handleStockShortageResolution = (method) => {
    const missingItems = buildMissingStockItems(calculationResults?.materials);
    if (missingItems.length === 0) {
      message.info(
        "Aucun stock manquant n'a été détecté. Relancez le calcul des besoins."
      );
      setStockShortageActionsVisible(false);
      return;
    }

    if (method === "purchase" && !canCreatePurchase) {
      message.error("Vous n'avez pas la permission de créer un achat.");
      return;
    }
    if (method === "adjustment" && !canCreateStockAdjustment) {
      message.error(
        "Vous n'avez pas la permission de créer un ajustement de stock."
      );
      return;
    }

    const currentFormValues = form.getFieldsValue([
      "production_unit_id",
      "output_quantity",
    ]);
    const replenishment = buildStockReplenishmentRequest({
      sourceType: "production",
      sourceLabel: selectedUnit?.name || "production",
      sourcePath: `${location.pathname}${location.search}`,
      companyId: selectedCompany,
      warehouseId: selectedWarehouse,
      items: missingItems,
      metadata: {
        production_unit_id:
          toPositiveInt(currentFormValues?.production_unit_id) ||
          toPositiveInt(selectedUnit?.id),
        production_unit_name: selectedUnit?.name || "",
        output_quantity:
          toPositiveNumber(currentFormValues?.output_quantity) ||
          toPositiveNumber(calculationResults?.output_quantity),
        return_context: {
          production_unit_id:
            toPositiveInt(currentFormValues?.production_unit_id) ||
            toPositiveInt(selectedUnit?.id),
          output_quantity:
            toPositiveNumber(currentFormValues?.output_quantity) ||
            toPositiveNumber(calculationResults?.output_quantity),
          restaurant_mode: restaurantMode,
          restaurant_ticket: restaurantTicketContext || null,
          sales_production_replenishment:
            salesProductionReplenishmentContext || null,
        },
      },
    });

    setStockShortageActionsVisible(false);
    navigate(
      method === "purchase"
        ? "/gestion-achats/achat"
        : "/gestion-stock?tab=adjustments",
      { state: { stockReplenishment: replenishment } }
    );
  };

  const renderGuardrailQuickActions = (guardrails) => {
    const configuredActions = Array.isArray(guardrails?.quick_actions)
      ? guardrails.quick_actions
      : [];
    const hasReliableMissingStock =
      buildMissingStockItems(calculationResults?.materials).length > 0;
    const requestedActions =
      !calculationResults?.all_available && hasReliableMissingStock
      ? [
          configuredActions.find((action) => action.type === "edit_recipe") || {
            code: "edit_recipe",
            type: "edit_recipe",
            label: `Ouvrir la ${productionTerminology.configurationSingular}`,
          },
        ]
      : configuredActions;
    const visibleActions = requestedActions.filter(canUseQuickAction);
    if (visibleActions.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: 16 }}>
        <Text strong>Actions disponibles dans le systeme</Text>
        <Space wrap style={{ display: "flex", marginTop: 8 }}>
          {visibleActions.map((action) => (
            <Button
              key={action.code}
              onClick={() => handleGuardrailQuickAction(action)}
            >
              {action.label}
            </Button>
          ))}
        </Space>
      </div>
    );
  };

  // Lancer la production
  const handleProduce = async () => {
    if (
      !canLaunchProduction
    ) {
      message.error(
        `Vous n'avez pas la permission de démarrer la ${productionTerminology.costContext}.`
      );
      return;
    }

    if (!selectedWarehouse) {
      message.error(
        `Veuillez sélectionner un établissement avant de lancer la ${productionTerminology.costContext}`
      );
      return;
    }

    if (!calculationResults || !calculationResults.all_available) {
      message.error(
        `Impossible de lancer la ${productionTerminology.costContext} car certains ${productionTerminology.materialPlural} sont insuffisants`
      );
      return;
    }

    const executeProduction = async (acknowledged = false) => {
      setProducing(true);
      try {
        const values = form.getFieldsValue();
        const user_id = user?.id || null;
        const payload = {
          ...values,
          user_id,
          restaurant_ticket_id: restaurantTicketContext?.id || null,
          is_regularization: regularizationContext ? 1 : 0,
          warehouse_id: selectedWarehouse,
          company_id: selectedCompany,
          notes: restaurantTicketContext?.id
            ? `Conversion du ticket cuisine #${restaurantTicketContext.id}`
            : regularizationContext
              ? `Régularisation de fin de journée (vente sans production)`
              : `${productionTerminology.processTitle} de ${values.output_quantity} unités`,
          recipe_version_hash:
            calculationResults?.recipe_summary?.version_hash || null,
          pricing_guard_acknowledged: acknowledged,
          __offline_preview: {
            production_unit_id: selectedUnit?.id || values.production_unit_id,
            production_unit_name:
              selectedUnit?.name || `Unite #${values.production_unit_id || ""}`,
            production_kind: selectedUnit?.production_kind || "finished",
            output_quantity: values.output_quantity,
            user_name: user?.name || "Utilisateur",
            cost_summary: {
              total_material_cost:
                Number(
                  calculationResults?.cost_summary?.estimated_material_cost
                ) || 0,
              total_variable_cost:
                Number(calculationResults?.cost_summary?.total_variable_cost) ||
                0,
              total_cost:
                Number(calculationResults?.cost_summary?.estimated_total_cost) ||
                0,
              unit_cost_real:
                Number(calculationResults?.cost_summary?.estimated_unit_cost) ||
                0,
            },
            materials: calculationResults?.materials || [],
            outputs: calculationResults?.outputs || [],
            variable_costs: calculationResults?.variable_costs || [],
            financial_tracking:
              calculationResults?.financial_tracking || null,
            offline_snapshot: calculationResults?.offline_snapshot || null,
          },
        };

        const processResult = await processProduction(payload);
        clearStockReplenishmentReturn();

        if (processResult?.offline) {
          message.info(
            productionTerminology.offlineSuccessMessage
          );
        } else {
          message.success(
            processResult?.production_code
              ? `${productionTerminology.successMessage} (Lot ${processResult.production_code})`
              : productionTerminology.successMessage
          );
        }

        setProductionResultData({
          ...calculationResults,
          production_code: processResult?.production_code || null,
          restaurant_ticket:
            processResult?.restaurant_ticket ||
            calculationResults?.restaurant_ticket ||
            null,
          materials:
            processResult?.materials_used || calculationResults?.materials || [],
          outputs:
            processResult?.outputs_produced || calculationResults?.outputs || [],
          cost_summary:
            processResult?.cost_summary ||
            calculationResults?.cost_summary ||
            null,
          variable_costs:
            processResult?.variable_costs_applied ||
            calculationResults?.variable_costs ||
            [],
          recipe_summary:
            processResult?.recipe_summary ||
            calculationResults?.recipe_summary ||
            null,
          pricing_guardrails:
            processResult?.pricing_guardrails ||
            calculationResults?.pricing_guardrails ||
            null,
          financial_tracking:
            processResult?.financial_tracking ||
            calculationResults?.financial_tracking ||
            null,
          sales_production_replenishment:
            salesProductionReplenishmentContext || null,
        });
        setProductionResultModalVisible(true);
        if (restaurantMode) {
          clearRestaurantTicketSelection();
        }
        // Nettoyer le contexte de regularisation (query params) pour qu'une
        // production manuelle ulterieure sur cette page ne soit pas marquee
        // par erreur comme une regularisation.
        if (regularizationContext) {
          navigate("/production/process", { replace: true });
        }

        form.setFieldsValue({
          production_unit_id: undefined,
          output_quantity: 1,
        });
        setSelectedUnit(null);
        setCalculationResults(null);
        setAdvancedDetailsOpen(false);
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          message.error(error.response.data.error);
        } else if (error.error) {
          message.error(error.error);
        } else {
          message.error(`Erreur lors de la ${productionTerminology.costContext}`);
        }
        console.error(error);
      } finally {
        setProducing(false);
      }
    };
    const guardrails = calculationResults?.pricing_guardrails;

    if (guardrails?.status === "blocked") {
      message.error(
        guardrails?.user_guidance?.title ||
          "Le lancement est bloque tant que les points signales ne sont pas traites."
      );
      return;
    }

    if (guardrails?.requires_acknowledgement) {
      Modal.confirm({
        title: "Confirmation requise",
        okText: "Confirmer et lancer",
        cancelText: "Annuler",
        width: 720,
        content: (
          <div>
            <p style={{ marginBottom: 12 }}>
              Cette {productionTerminology.costContext} peut etre lancee, mais une verification est
              demandee avant validation.
            </p>
            {renderGuardrailDescription(guardrails)}
          </div>
        ),
        onOk: () => executeProduction(true),
      });
      return;
    }

    await executeProduction(false);
  };

  const handleCancelProduction = async (productionId) => {
    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Historique.delete"
      )
    ) {
      message.error(
        `Vous n'avez pas la permission d'annuler la ${productionTerminology.costContext}.`
      );
      return;
    }
    // Placeholder: Implement actual cancel logic
    message.info(
      `Tentative d'annulation de la ${productionTerminology.costContext} ${productionId} (logique à implémenter)`
    );
    // try {
    //   await cancelProduction(productionId);
    //   message.success("Production annulée");
    //   // Refresh or update state
    // } catch (error) { message.error("Erreur d'annulation"); }
  };

  const handleFinalizeProduction = async (productionId) => {
    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Gerer.finalize"
      )
    ) {
      message.error(
        `Vous n'avez pas la permission de finaliser la ${productionTerminology.costContext}.`
      );
      return;
    }
    // Placeholder: Implement actual finalize logic
    message.info(
      `Tentative de finalisation de la ${productionTerminology.costContext} ${productionId} (logique à implémenter)`
    );
    // try {
    //   await finalizeProduction(productionId);
    //   message.success("Production finalisée");
    //   // Refresh or update state
    // } catch (error) { message.error("Erreur de finalisation"); }
  };

  const renderLotAllocations = (
    allocations = [],
    maxVisible = 3,
    unitShortName = null
  ) => {
    if (!Array.isArray(allocations) || allocations.length === 0) {
      return <Text type="secondary">-</Text>;
    }

    const visible = allocations.slice(0, maxVisible);
    const hiddenCount = Math.max(allocations.length - maxVisible, 0);

    return (
      <Space size={4} wrap>
        {visible.map((allocation, index) => {
          const lotCode =
            allocation?.lot_code || allocation?.lotId || allocation?.lot_id;
          const quantity = Number(
            allocation?.quantity_allocated ?? allocation?.quantity ?? 0
          );
          const renderedQty = Number.isFinite(quantity)
            ? quantity.toFixed(2)
            : "0.00";

          return (
            <Tooltip
              key={`${lotCode || "lot"}-${index}`}
              title={renderLotAllocationTooltip(allocation, unitShortName)}
            >
              <Tag color="blue" style={{ cursor: "help" }}>
                {lotCode || "Lot"}: {renderedQty}
              </Tag>
            </Tooltip>
          );
        })}
        {hiddenCount > 0 && <Tag>+{hiddenCount}</Tag>}
      </Space>
    );
  };

  const pricingGuardrails = calculationResults?.pricing_guardrails || null;
  const lotWatchItems = Array.isArray(
    pricingGuardrails?.user_guidance?.lot_watch_items
  )
    ? pricingGuardrails.user_guidance.lot_watch_items
    : [];
  const lotWatchProductNames = getLotWatchProductNames(pricingGuardrails);
  const decisionMaterials = sortMaterialsForDecision(
    calculationResults?.materials,
    lotWatchProductNames
  );
  const missingMaterials = decisionMaterials.filter(
    (material) => getMaterialAttentionState(material, lotWatchProductNames) === "shortage"
  );
  const firstMissingMaterial = missingMaterials[0] || null;
  const guardrailStatus = pricingGuardrails?.status || "ok";
  const isDecisionBlocked =
    Boolean(calculationResults) &&
    (!calculationResults?.all_available || guardrailStatus === "blocked");
  const isDecisionWarning =
    Boolean(calculationResults) && !isDecisionBlocked && guardrailStatus === "warning";
  const decisionTone = isDecisionBlocked
    ? "blocked"
    : isDecisionWarning
      ? "warning"
      : "ready";
  const decisionTitle = isDecisionBlocked
    ? "Blocage à résoudre"
    : isDecisionWarning
      ? "Vérification requise"
      : "Prêt à lancer";

  let decisionDescription = `Les contrôles de ${productionTerminology.costContext} sont validés.`;
  if (missingMaterials.length === 1) {
    const missingQuantity = getMaterialMissingQuantity(firstMissingMaterial);
    decisionDescription = `Il manque ${formatQuantity(missingQuantity, {
      minimumFractionDigits: 2,
    })} ${
      firstMissingMaterial?.unit_short_name || "unité"
    } de ${lowercaseFirstLetter(firstMissingMaterial?.product_name)}.`;
  } else if (missingMaterials.length > 1) {
    decisionDescription = `${missingMaterials.length} ${productionTerminology.materialPlural} nécessitent un approvisionnement.`;
  } else if (guardrailStatus === "blocked" || guardrailStatus === "warning") {
    decisionDescription =
      pricingGuardrails?.user_guidance?.summary ||
      (guardrailStatus === "blocked"
        ? "Les contrôles signalés doivent être résolus avant le lancement."
        : "Relisez les points signalés avant de confirmer le lancement.");
  }

  const launchDisabledReason = getLaunchDisabledReason({
    hasResults: Boolean(calculationResults),
    allAvailable: Boolean(calculationResults?.all_available),
    guardrailStatus,
    hasWarehouse: Boolean(selectedWarehouse),
    producing,
    canLaunch: canLaunchProduction,
    contextLabel: productionTerminology.costContext,
  });
  const canLaunchNow = !launchDisabledReason;
  const costSummary = calculationResults?.cost_summary || null;
  const plannedTotalCost = Number(
    costSummary?.estimated_total_cost ?? costSummary?.estimated_material_cost
  );
  const standardTotalCost = Number(
    costSummary?.standard_total_cost ?? costSummary?.standard_material_cost
  );
  const costDeltaAmount = Number(costSummary?.delta_amount) || 0;
  const costDeltaRatio = Number(costSummary?.delta_ratio) || 0;
  const outputUnitShortName =
    calculationResults?.outputs?.[0]?.unit_short_name ||
    selectedUnit?.outputs?.[0]?.unit_short_name ||
    "";
  const visibleRevaluationAction = Array.isArray(pricingGuardrails?.quick_actions)
    ? pricingGuardrails.quick_actions.find(
        (action) => action?.type === "revalue_lot" && canUseQuickAction(action)
      )
    : null;
  const canEditSelectedRecipe = canUseQuickAction({ type: "edit_recipe" });
  const showQuantityAdjustment =
    !displayedRestaurantTicket?.id && !regularizationContext;

  const handleOpenAdvancedDetails = () => {
    setAdvancedDetailsOpen(true);
  };

  // Colonnes pour la table des intrants
  const materialColumns = [
    {
      title: productionTerminology.materialSingularTitle,
      dataIndex: "product_name",
      key: "product_name",
      render: (text, record) => (
        <Space>
          {record.image && (
            <Avatar
              src={record.image}
              size="small"
            />
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: "Quantité nécessaire",
      dataIndex: "required_quantity",
      key: "required_quantity",
      align: "right",
      render: (text, record) => (
        <span>
          {text} {record.unit_short_name}
        </span>
      ),
    },
    {
      title: "Stock qui sera consomme",
      key: "lot_preview",
      render: (_, record) => {
        if (!record?.lot_tracking_enabled) {
          return <Text type="secondary">N/A</Text>;
        }

        const allocations = record?.lot_allocations_preview || [];
        const missingQty = Number(record?.lot_missing_quantity) || 0;
        const simulatedLegacyQty =
          Number(record?.simulated_legacy_coverage_quantity) || 0;

        return (
          <div>
            {renderLotAllocations(allocations, 3, record?.unit_short_name)}
            {simulatedLegacyQty > 0 && (
              <div style={{ marginTop: 4 }}>
                <Tag color="gold">
                  Ancien stock: {simulatedLegacyQty.toFixed(2)}
                </Tag>
              </div>
            )}
            {missingQty > 0 && (
              <div style={{ marginTop: 4 }}>
                <Tag color="red">Stock manque: {missingQty.toFixed(2)}</Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Prix habituel",
      dataIndex: "standard_purchase_price",
      key: "standard_purchase_price",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Prix prevu",
      dataIndex: "estimated_purchase_price",
      key: "estimated_purchase_price",
      align: "right",
      render: (value, record) => (
        <Space size={4}>
          <span>{formatCurrency(value)}</span>
          {record.lot_tracking_enabled ? (
            <Tag color="blue">Stock actuel</Tag>
          ) : (
            <Tag>Prix fiche</Tag>
          )}
        </Space>
      ),
    },
    {
      title: `Coût prévu (${productionTerminology.materialSingular})`,
      key: "total_material_purchase_cost",
      align: "right",
      render: (_, record) => {
        const estimatedCost = Number(
          record.estimated_required_cost ?? record.required_cost
        );
        if (Number.isFinite(estimatedCost)) {
          return formatCurrency(estimatedCost);
        }
        return "N/A";
      },
    },
    {
      title: "Stock disponible",
      dataIndex: "available",
      key: "available",
      align: "right",
      render: (text, record) => (
        <span>
          {text} {record.unit_short_name}
        </span>
      ),
    },
    {
      title: "Statut",
      key: "status",
      align: "center",
      render: (_, record) =>
        record.is_sufficient ? (
          <CheckCircleOutlined style={{ color: "green", fontSize: 18 }} />
        ) : (
          <CloseCircleOutlined style={{ color: "red", fontSize: 18 }} />
        ),
    },
  ];

  const decisionMaterialColumns = [
    {
      title: productionTerminology.materialSingularTitle,
      dataIndex: "product_name",
      key: "product_name",
      width: 220,
      render: (text, record) => (
        <div className="production-process-desktop__material-name">
          <Avatar
            className="production-process-desktop__material-avatar"
            src={record.image}
            icon={!record.image ? <AppstoreOutlined /> : undefined}
            size={34}
          />
          <span title={text}>{text}</span>
        </div>
      ),
    },
    {
      title: calculationResults
        ? `Besoin (pour ${formatQuantity(calculationResults.output_quantity)} ${outputUnitShortName})`
        : "Besoin",
      dataIndex: "required_quantity",
      key: "required_quantity",
      width: 120,
      align: "right",
      render: (value, record) => (
        <span>
          {formatQuantity(value)} {record.unit_short_name}
        </span>
      ),
    },
    {
      title: "Stock disponible",
      dataIndex: "available",
      key: "available",
      width: 160,
      align: "right",
      render: (value, record) => {
        const state = getMaterialAttentionState(record, lotWatchProductNames);
        const missingQuantity = getMaterialMissingQuantity(record);

        return (
          <div
            className={`production-process-desktop__stock-cell${
              state === "shortage"
                ? " production-process-desktop__stock-cell--shortage"
                : ""
            }`}
          >
            <span>
              {formatQuantity(value, {
                minimumFractionDigits: state === "shortage" ? 2 : 0,
              })}{" "}
              {record.unit_short_name}
            </span>
            {state === "shortage" && (
              <span className="production-process-desktop__cell-note">
                manque{" "}
                {formatQuantity(missingQuantity, {
                  minimumFractionDigits: 2,
                })}{" "}
                {record.unit_short_name}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <span className="production-process-desktop__cost-heading">
          Lot / coût
          <Tooltip title="Coût unitaire estimé à partir du stock qui sera consommé">
            <InfoCircleOutlined aria-label="Informations sur le coût" />
          </Tooltip>
        </span>
      ),
      key: "lot_cost",
      width: 150,
      align: "right",
      render: (_, record) => {
        const plannedUnitCost =
          record.estimated_purchase_price ?? record.purchase_price;

        return (
          <div className="production-process-desktop__cost-cell">
            <span>{formatCurrency(plannedUnitCost)}</span>
          </div>
        );
      },
    },
    {
      title: "Statut",
      key: "decision_status",
      width: 150,
      render: (_, record) => {
        const state = getMaterialAttentionState(record, lotWatchProductNames);
        const statusConfig = {
          shortage: {
            label: "À traiter",
            icon: <WarningFilled />,
          },
          watch: {
            label: "Prix à vérifier",
            icon: <ExclamationCircleOutlined />,
          },
          available: {
            label: "Disponible",
            icon: <CheckCircleOutlined />,
          },
        }[state];

        return (
          <span
            className={`production-process-desktop__status production-process-desktop__status--${state}`}
            role="status"
          >
            {statusConfig.icon}
            {statusConfig.label}
          </span>
        );
      },
    },
  ];

  const variableCostColumns = [
    {
      title: "Libelle",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Cout unitaire",
      dataIndex: "cost_per_unit",
      key: "cost_per_unit",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Cout total",
      dataIndex: "total_cost",
      key: "total_cost",
      align: "right",
      render: (value) => formatCurrency(value),
    },
  ];

  // Colonnes pour la table des produits finis
  const outputColumns = [
    {
      title: "Produit fini",
      dataIndex: "product_name",
      key: "product_name",
      render: (text, record) => (
        <Space>
          {record.image && (
            <Avatar
              src={record.image}
              size="small"
            />
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: "Quantité produite",
      dataIndex: "result_quantity",
      key: "result_quantity",
      align: "right",
      render: (text, record) => (
        <span>
          {text} {record.unit_short_name}
        </span>
      ),
    },
  ];

  const navigateToSalesAfterProduction = (replenishment) => {
    const navigation = buildStockReplenishmentReturnNavigation(
      replenishment,
      "production"
    );
    if (!navigation) {
      message.error("Impossible de retrouver le panier de vente d’origine.");
      return;
    }

    setProductionResultModalVisible(false);
    navigate(navigation.path, { state: navigation.state });
  };

  const navigateToNextSalesProduction = (replenishment) => {
    const step = getSalesProductionReplenishmentStep(replenishment);
    const navigation = step?.has_next
      ? buildSalesProductionReplenishmentNavigation(
          replenishment,
          step.current_index + 1
        )
      : null;
    if (!navigation) {
      navigateToSalesAfterProduction(replenishment);
      return;
    }

    setProductionResultModalVisible(false);
    navigate(navigation.path, { state: navigation.state });
  };

  const salesReturnNavigation = salesProductionReplenishmentContext
    ? buildStockReplenishmentReturnNavigation(
        salesProductionReplenishmentContext,
        "production"
      )
    : null;
  const resultSalesProductionReplenishment =
    productionResultData?.sales_production_replenishment || null;
  const resultSalesProductionStep = getSalesProductionReplenishmentStep(
    resultSalesProductionReplenishment
  );
  const backPath = restaurantMode ? "/production/kitchen" : "/production/units";
  const handleBack = () => {
    if (salesReturnNavigation) {
      navigate(salesReturnNavigation.path, {
        state: salesReturnNavigation.state,
      });
      return;
    }
    navigate(backPath);
  };
  const headerTitle = restaurantMode
    ? "Conversion cuisine -> production"
    : productionTerminology.manageTitle;
  const headerSubtitle = restaurantMode
    ? "Vérifier les besoins et convertir ce ticket en production"
    : regularizationContext
      ? "Vérifier et régulariser la préparation de fin de journée"
      : productionTerminology.costContext === "cuisine"
        ? "Planifier, vérifier et lancer la préparation"
        : "Planifier, vérifier et lancer la production";

  if (restaurantMode && !isRestaurantActivity) {
    return (
      <Card>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/production/kitchen")}
            style={{ marginRight: 16 }}
          >
            Retour
          </Button>
          <Title level={3}>{headerTitle}</Title>
        </div>
        <Alert
          type="warning"
          showIcon
          message="Ecran reserve a la restauration"
          description="La conversion des tickets cuisine n'est disponible que pour les entreprises configurees avec le profil d'activite Restauration."
        />
      </Card>
    );
  }

  if (restaurantMode && !displayedRestaurantTicket?.id && !loading) {
    return (
      <Card>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ marginRight: 16 }}
          >
            Retour
          </Button>
          <Title level={3}>{headerTitle}</Title>
        </div>
        <Alert
          type="info"
          showIcon
          message="Aucun ticket cuisine selectionne"
          description="Ouvrez un ticket depuis la file cuisine pour lancer d'abord le calcul, puis la conversion en production avec le bon restaurant_ticket_id."
        />
      </Card>
    );
  }

  if (
    restaurantMode &&
    displayedRestaurantTicket?.id &&
    !displayedRestaurantTicket?.production_unit_id &&
    !loading
  ) {
    return (
      <Card>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(backPath)}
            style={{ marginRight: 16 }}
          >
            Retour
          </Button>
          <Title level={3}>{headerTitle}</Title>
        </div>
        <Alert
          type="error"
          showIcon
          message="Ticket non convertible"
          description={`Ce ticket ne reference pas d'${productionTerminology.unitSingular} exploitable. Retournez dans la file cuisine pour reevaluer la commande.`}
        />
      </Card>
    );
  }

  // Rendu conditionnel si aucun magasin n'est sélectionné
  if (!selectedWarehouse && !loading) {
    return (
      <Card>
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 16 }}
        >
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(backPath)}
            style={{ marginRight: 16 }}
          >
            Retour
          </Button>
          <Title level={3}>{headerTitle}</Title>
        </div>

        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Empty
            image={
              <ExclamationCircleOutlined
                style={{ fontSize: 64, color: "#faad14" }}
              />
            }
            description={
              <div>
                <h3
                  style={{ fontSize: 18, fontWeight: "bold", marginBottom: 8 }}
                >
                  Aucun établissement sélectionné
                </h3>
                <p style={{ color: "#666", marginBottom: 16 }}>
                  Veuillez sélectionner un établissement dans la barre de navigation
                  pour accéder au module de {productionTerminology.costContext}.
                </p>
                <Alert
                  message="Information importante"
                  description={`La gestion de la ${productionTerminology.costContext} est liée à l'établissement sélectionné. Chaque établissement a ses propres ${productionTerminology.unitPlural} et stocks de ${productionTerminology.materialPlural}.`}
                  type="info"
                  showIcon
                  style={{ textAlign: "left", maxWidth: 500, margin: "0 auto" }}
                />
              </div>
            }
          />
        </div>
      </Card>
    );
  }

  if (loading && !units.length) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="production-process-desktop">
      <header className="production-process-desktop__header">
        <div className="production-process-desktop__heading">
          <Button
            className="production-process-desktop__back"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            Retour
          </Button>
          <div>
            <Title level={2} className="production-process-desktop__title">
              {headerTitle}
            </Title>
            <Text className="production-process-desktop__subtitle">
              {headerSubtitle}
            </Text>
          </div>
        </div>

        {selectedWarehouseName && (
          <div className="production-process-desktop__warehouse">
            <ShopOutlined />
            <span>{selectedWarehouseName}</span>
          </div>
        )}
      </header>

      {(regularizationContext ||
        displayedRestaurantTicket?.id ||
        stockReplenishmentReturnContext ||
        salesProductionStep) && (
        <div className="production-process-desktop__context-alerts">
          {salesProductionStep && (
            <Alert
              type="info"
              showIcon
              message={`Réapprovisionnement du panier — étape ${
                salesProductionStep.current_index + 1
              } sur ${salesProductionStep.total}`}
              description={`Préparez ${salesProductionStep.item.output_quantity} ${
                salesProductionStep.item.unit_short_name ||
                salesProductionStep.item.unit_name ||
                "unité(s)"
              } de ${
                salesProductionStep.item.product_name || "ce produit"
              }. Après validation, vous pourrez ${
                salesProductionStep.has_next
                  ? "enchaîner avec le produit fabriqué suivant"
                  : "retourner directement au panier d’origine"
              }.`}
            />
          )}

          {stockReplenishmentReturnContext && (
            <Alert
              type={
                !calculationResults
                  ? "info"
                  : calculationResults.all_available
                    ? "success"
                    : "warning"
              }
              showIcon
              message={
                !calculationResults
                  ? "Stock réapprovisionné — recalcul en cours"
                  : calculationResults.all_available
                    ? "Stock désormais suffisant"
                    : "Réapprovisionnement encore insuffisant"
              }
              description={
                !calculationResults
                  ? "L’unité et la quantité ont été restaurées. Les besoins sont recalculés avec le stock actualisé."
                  : calculationResults.all_available
                    ? "La production est prête. Vérifiez le résultat puis utilisez le bouton de lancement pour confirmer."
                    : "Le stock actualisé ne couvre pas encore tous les besoins. Les quantités restantes sont indiquées ci-dessous."
              }
            />
          )}

          {regularizationContext && (
            <Alert
              type="warning"
              showIcon
              message="Régularisation de fin de journée"
              description={`La quantité ci-dessous correspond au déficit vendu sans production. Vérifiez la consommation des ${productionTerminology.materialPlural}, puis validez pour solder le stock.`}
            />
          )}

          {displayedRestaurantTicket?.id && (
            <Alert
              type="info"
              showIcon
              message={`Ticket cuisine #${displayedRestaurantTicket.id}`}
              description={
                <Space wrap>
                  {displayedRestaurantTicket.order_number && (
                    <Tag color="blue">
                      Commande: {displayedRestaurantTicket.order_number}
                    </Tag>
                  )}
                  {displayedRestaurantTicket.product_name && (
                    <Tag color="purple">
                      Article: {displayedRestaurantTicket.product_name}
                    </Tag>
                  )}
                  {displayedRestaurantTicket.table_label && (
                    <Tag>Table: {displayedRestaurantTicket.table_label}</Tag>
                  )}
                  {displayedRestaurantTicket.waiter_name && (
                    <Tag>Serveur: {displayedRestaurantTicket.waiter_name}</Tag>
                  )}
                  {displayedRestaurantTicket.requested_quantity && (
                    <Tag color="gold">
                      Demande: {displayedRestaurantTicket.requested_quantity}
                    </Tag>
                  )}
                  {displayedRestaurantTicket.eligible_quantity && (
                    <Tag color="green">
                      Éligible: {displayedRestaurantTicket.eligible_quantity}
                    </Tag>
                  )}
                  {displayedRestaurantTicket.status && (
                    <Tag color="blue">
                      Statut: {displayedRestaurantTicket.status}
                    </Tag>
                  )}
                </Space>
              }
            />
          )}
        </div>
      )}

      <div
        className={`production-process-desktop__layout${
          calculationResults ? " production-process-desktop__layout--with-summary" : ""
        }`}
      >
        <Form
          className="production-process-desktop__setup"
          form={form}
          layout="vertical"
          onFinish={handleCalculate}
          onValuesChange={handleFormValuesChange}
          initialValues={{
            production_unit_id:
              displayedRestaurantTicket?.production_unit_id ||
              (id ? parseInt(id, 10) : undefined),
            output_quantity: displayedRestaurantTicket?.output_quantity || 1,
          }}
        >
          <div className="production-process-desktop__setup-grid">
            <div className="production-process-desktop__recipe">
              <div className="production-process-desktop__recipe-control-row">
                <Form.Item
                  className="production-process-desktop__recipe-field"
                  name="production_unit_id"
                  label={productionTerminology.unitSingularTitle}
                  rules={[
                    {
                      required: true,
                      message: `Veuillez sélectionner une ${productionTerminology.unitSingular}`,
                    },
                  ]}
                >
                  <Select
                    placeholder={
                      !selectedWarehouse
                        ? "Sélectionnez d'abord un établissement"
                        : units.length === 0
                          ? `Aucune ${productionTerminology.unitSingular} disponible`
                          : `Sélectionner une ${productionTerminology.unitSingular}`
                    }
                    loading={loading}
                    onChange={handleUnitChange}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      matchesProductionUnitSearch(option?.label, input)
                    }
                    labelRender={({ label, value }) => {
                      const labelledUnit = units.find(
                        (unit) => Number(unit.id) === Number(value)
                      );

                      return (
                        <span className="production-process-desktop__selected-recipe">
                          <Avatar
                            src={labelledUnit?.image}
                            icon={!labelledUnit?.image ? <RestOutlined /> : undefined}
                            size={30}
                          />
                          <span>{labelledUnit?.name || label}</span>
                        </span>
                      );
                    }}
                    disabled={
                      !selectedWarehouse || Boolean(displayedRestaurantTicket?.id)
                    }
                    notFoundContent={
                      !selectedWarehouse ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Sélectionnez un établissement"
                        />
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={`${productionTerminology.noUnitFound} pour cet établissement`}
                        />
                      )
                    }
                  >
                    {units.map((unit) => (
                      <Option key={unit.id} value={unit.id} label={unit.name}>
                        <Space size={8}>
                          <Avatar
                            src={unit.image}
                            icon={!unit.image ? <RestOutlined /> : undefined}
                            size={28}
                          />
                          <span>{unit.name}</span>
                          <Tag
                            color={
                              unit.production_kind === PRODUCTION_KIND_SEMI_FINISHED
                                ? "gold"
                                : "blue"
                            }
                          >
                            {getProductionKindLabel(unit.production_kind)}
                          </Tag>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedUnit && (
                  <div className="production-process-desktop__recipe-meta">
                    <Tag
                      color={
                        selectedUnit.production_kind === PRODUCTION_KIND_SEMI_FINISHED
                          ? "gold"
                          : "blue"
                      }
                    >
                      {getProductionKindLabel(selectedUnit.production_kind)}
                    </Tag>
                    {selectedUnit.recipe_summary?.version_label && (
                      <>
                        <span className="production-process-desktop__meta-separator">
                          |
                        </span>
                        <span>
                          Version : {selectedUnit.recipe_summary.version_label}
                        </span>
                      </>
                    )}
                    {selectedUnit.recipe_summary?.updated_at && (
                      <>
                        <span className="production-process-desktop__meta-separator">
                          |
                        </span>
                        <span>
                          Mis à jour le{" "}
                          {new Date(
                            selectedUnit.recipe_summary.updated_at
                          ).toLocaleString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </>
                    )}
                    {selectedUnit.recipe_summary?.modified_recently && (
                      <Tag color="volcano">Modifiée récemment</Tag>
                    )}
                  </div>
                )}
              </div>
            </div>

            {selectedUnit && (
              <div className="production-process-desktop__quantity">
                <Form.Item
                  name="output_quantity"
                  label={productionTerminology.outputQuantityLabel}
                  rules={[
                    { required: true, message: "Veuillez saisir une quantité" },
                  ]}
                >
                  <InputNumber
                    min={0.01}
                    max={MAX_PRODUCTION_OUTPUT_QUANTITY}
                    step={0.01}
                    precision={2}
                    disabled={Boolean(displayedRestaurantTicket?.id)}
                    addonAfter={selectedUnit.outputs?.[0]?.unit_short_name}
                  />
                </Form.Item>
              </div>
            )}

            {selectedUnit && (
              <div className="production-process-desktop__calculate">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  disabled={!selectedWarehouse}
                >
                  {calculationResults ? "Recalculer" : "Calculer les besoins"}
                </Button>
                {showQuantityAdjustment && (
                  <Button
                    className="production-process-desktop__capacity-link"
                    type="link"
                    icon={<CalculatorOutlined />}
                    onClick={() => setCapacityCalculatorVisible(true)}
                  >
                    Calculer selon mes {productionTerminology.materialPlural}
                  </Button>
                )}
              </div>
            )}
          </div>
        </Form>

        {calculationResults && (
          <>
            <main className="production-process-desktop__main">
            <div className="production-process-desktop__section-heading">
              <Title level={4}>
                {productionTerminology.materialPluralTitle} et besoins pour{" "}
                {formatQuantity(calculationResults.output_quantity, {
                  minimumFractionDigits: 2,
                })}{" "}
                {outputUnitShortName}
              </Title>
              <span className="production-process-desktop__section-count">
                {decisionMaterials.length}{" "}
                {decisionMaterials.length > 1
                  ? productionTerminology.materialPlural
                  : productionTerminology.materialSingular}
              </span>
            </div>

            {(calculationResults.local_estimate ||
              calculationResults.recipe_summary?.modified_recently ||
              Number(
                calculationResults.financial_tracking
                  ?.pending_regularization_count
              ) > 0) && (
              <div className="production-process-desktop__context-alerts">
                {calculationResults.local_estimate && (
                  <Alert
                    message="Calcul hors ligne reconstruit localement"
                    description={
                      calculationResults.offline_warning ||
                      `Le calcul a été reconstruit à partir de la ${productionTerminology.configurationSingular} et du dernier contexte local mémorisé.`
                    }
                    type="warning"
                    showIcon
                  />
                )}

                {calculationResults.recipe_summary?.modified_recently && (
                  <Alert
                    message={productionTerminology.configurationReviewTitle}
                    description={`La ${productionTerminology.configurationSingular} utilisée pour ce calcul a été mise à jour le ${new Date(
                      calculationResults.recipe_summary.updated_at
                    ).toLocaleString(
                      "fr-FR"
                    )}. Vérifiez les quantités avant de lancer.`}
                    type="warning"
                    showIcon
                  />
                )}

                {Number(
                  calculationResults.financial_tracking
                    ?.pending_regularization_count
                ) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Régularisation financière ultérieure nécessaire"
                    description={
                      calculationResults.financial_tracking.warning ||
                      "Les coûts concernés restent inclus dans le coût de production, mais aucune dépense ne sera créée tant que leur fournisseur et leur catégorie ne sont pas complétés."
                    }
                  />
                )}
              </div>
            )}

            <div className="production-process-desktop__materials-table">
              <Table
                columns={decisionMaterialColumns}
                dataSource={decisionMaterials}
                rowKey={(record) =>
                  record.id ||
                  record.product_id ||
                  `${record.product_name}-${record.unit_short_name || "unit"}`
                }
                pagination={false}
                size="middle"
                scroll={{ x: 800 }}
                rowClassName={(record) =>
                  getMaterialAttentionState(record, lotWatchProductNames) ===
                  "shortage"
                    ? "production-process-desktop__row--shortage"
                    : ""
                }
                locale={{
                  emptyText: `Aucun ${productionTerminology.materialSingular} à afficher`,
                }}
              />
            </div>

            {lotWatchItems.length > 0 && (
              <div className="production-process-desktop__watch-strip">
                <div className="production-process-desktop__watch-copy">
                  <InfoCircleOutlined />
                  <span>
                    {lotWatchItems.length} prix à vérifier
                    {lotWatchProductNames.length > 0
                      ? ` : ${lotWatchProductNames.join(", ")}`
                      : ""}
                  </span>
                </div>
                <Button type="link" onClick={handleOpenAdvancedDetails}>
                  Voir le détail
                </Button>
              </div>
            )}

            <Collapse
              className="production-process-desktop__details"
              activeKey={advancedDetailsOpen ? ["details"] : []}
              onChange={(keys) =>
                setAdvancedDetailsOpen(
                  Array.isArray(keys)
                    ? keys.includes("details")
                    : keys === "details"
                )
              }
              items={[
                {
                  key: "details",
                  label: "Détail des lots et répartition des coûts",
                  children: (
                    <div className="production-process-desktop__details-stack">
                      {guardrailStatus !== "ok" && (
                        <Alert
                          message={
                            pricingGuardrails?.user_guidance?.title ||
                            (guardrailStatus === "blocked"
                              ? `${productionTerminology.processTitle} bloquée temporairement`
                              : "Vérification conseillée avant lancement")
                          }
                          description={
                            <div>
                              {renderGuardrailDescription(pricingGuardrails)}
                              {renderGuardrailQuickActions(pricingGuardrails)}
                            </div>
                          }
                          type={
                            guardrailStatus === "blocked" ? "error" : "warning"
                          }
                          showIcon
                        />
                      )}

                      {renderLotWatchList(pricingGuardrails) && (
                        <Alert
                          message="Stocks à vérifier"
                          description={renderLotWatchList(pricingGuardrails)}
                          type="info"
                          showIcon
                        />
                      )}

                      <section>
                        <Title
                          level={5}
                          className="production-process-desktop__detail-title"
                        >
                          Lots et coûts par{" "}
                          {productionTerminology.materialSingular}
                        </Title>
                        <Table
                          columns={materialColumns}
                          dataSource={calculationResults.materials}
                          rowKey={(record) =>
                            record.id ||
                            record.product_id ||
                            `detail-${record.product_name}`
                          }
                          pagination={false}
                          size="small"
                          scroll={{ x: 1120 }}
                        />
                      </section>

                      {calculationResults.variable_costs?.length > 0 && (
                        <section>
                          <Title
                            level={5}
                            className="production-process-desktop__detail-title"
                          >
                            Coûts variables additionnels
                          </Title>
                          <Table
                            columns={variableCostColumns}
                            dataSource={calculationResults.variable_costs}
                            rowKey={(record) =>
                              record.id
                                ? record.id.toString()
                                : `${record.label}-${record.cost_per_unit}`
                            }
                            pagination={false}
                            size="small"
                          />
                        </section>
                      )}

                      {costSummary && (
                        <section>
                          <Title
                            level={5}
                            className="production-process-desktop__detail-title"
                          >
                            Répartition complète des coûts
                          </Title>
                          <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item
                              label={`Coût habituel des ${productionTerminology.materialPlural}`}
                            >
                              {formatCurrency(costSummary.standard_material_cost)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Coût prévu avec le stock actuel">
                              {formatCurrency(costSummary.estimated_material_cost)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Coût variable">
                              {formatCurrency(costSummary.total_variable_cost)}
                            </Descriptions.Item>
                            {Number(costSummary.total_depreciation_cost) > 0 && (
                              <Descriptions.Item label="Amortissement matériel">
                                {formatCurrency(
                                  costSummary.total_depreciation_cost
                                )}
                              </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Écart avec le coût habituel">
                              {formatSignedCurrency(costDeltaAmount)} (
                              {formatPercent(costDeltaRatio)})
                            </Descriptions.Item>
                            <Descriptions.Item label="Coût total habituel">
                              {formatCurrency(costSummary.standard_total_cost)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Coût total prévu">
                              {formatCurrency(costSummary.estimated_total_cost)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Coût unitaire habituel">
                              {formatCurrency(costSummary.standard_unit_cost)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Coût unitaire prévu">
                              {formatCurrency(costSummary.estimated_unit_cost)}
                            </Descriptions.Item>
                          </Descriptions>
                        </section>
                      )}

                      {calculationResults.outputs?.length > 0 && (
                        <section>
                          <Title
                            level={5}
                            className="production-process-desktop__detail-title"
                          >
                            {productionTerminology.outputProductsObtained}
                          </Title>
                          <Table
                            columns={outputColumns}
                            dataSource={calculationResults.outputs}
                            rowKey={(record) =>
                              record.id ||
                              record.product_id ||
                              `output-${record.product_name}`
                            }
                            pagination={false}
                            size="small"
                          />
                        </section>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </main>

          <aside className="production-process-desktop__summary">
            <div className="production-process-desktop__summary-title">
              <RestOutlined />
              {productionTerminology.costContext === "cuisine"
                ? "Résumé de la préparation"
                : "Résumé de la production"}
            </div>
            <div className="production-process-desktop__summary-body">
              <div
                className={`production-process-desktop__decision production-process-desktop__decision--${decisionTone}`}
              >
                <span className="production-process-desktop__decision-icon">
                  {decisionTone === "blocked" ? (
                    <WarningFilled />
                  ) : decisionTone === "warning" ? (
                    <ExclamationCircleOutlined />
                  ) : (
                    <CheckCircleOutlined />
                  )}
                </span>
                <div>
                  <span className="production-process-desktop__decision-title">
                    {decisionTitle}
                  </span>
                  <div className="production-process-desktop__decision-copy">
                    {decisionDescription}
                  </div>
                </div>
              </div>

              <div className="production-process-desktop__quantity-summary">
                <span className="production-process-desktop__summary-label">
                  {productionTerminology.costContext === "cuisine"
                    ? "Préparation"
                    : productionTerminology.requestedQuantityLabel}
                </span>
                <span className="production-process-desktop__quantity-value">
                  {formatQuantity(calculationResults.output_quantity, {
                    minimumFractionDigits: 2,
                  })}
                  <span className="production-process-desktop__quantity-unit">
                    {outputUnitShortName}
                  </span>
                </span>
              </div>

              {costSummary && (
                <div className="production-process-desktop__costs">
                  <span className="production-process-desktop__summary-label">
                    Coûts prévus
                  </span>
                  <div className="production-process-desktop__cost-row">
                    <span>Coût prévu</span>
                    <strong>{formatCurrency(plannedTotalCost)}</strong>
                  </div>
                  <div className="production-process-desktop__cost-row">
                    <span>Coût habituel</span>
                    <strong>{formatCurrency(standardTotalCost)}</strong>
                  </div>
                  <div
                    className={`production-process-desktop__cost-row ${
                      costDeltaAmount > 0
                        ? "production-process-desktop__cost-row--negative"
                        : costDeltaAmount < 0
                          ? "production-process-desktop__cost-row--positive"
                          : ""
                    }`}
                  >
                    <span>Écart</span>
                    <strong>
                      {formatSignedCurrency(costDeltaAmount)} (
                      {costDeltaAmount > 0 ? "+" : ""}
                      {formatPercent(costDeltaRatio)})
                    </strong>
                  </div>
                </div>
              )}

              {(missingMaterials.length > 0 ||
                visibleRevaluationAction ||
                showQuantityAdjustment ||
                canEditSelectedRecipe) && (
                <div className="production-process-desktop__actions">
                  {missingMaterials.length > 0 &&
                    (canCreatePurchase || canCreateStockAdjustment) && (
                      <Button
                        type="primary"
                        icon={<ShoppingCartOutlined />}
                        onClick={() =>
                          handleGuardrailQuickAction({
                            type: "replenish_missing_stock",
                          })
                        }
                      >
                        {missingMaterials.length === 1
                          ? `Approvisionner le ${lowercaseFirstLetter(
                              firstMissingMaterial?.product_name
                            )}`
                          : `Approvisionner les ${productionTerminology.materialPlural}`}
                      </Button>
                    )}

                  {visibleRevaluationAction && (
                    <Button
                      type={missingMaterials.length > 0 ? "default" : "primary"}
                      icon={<ReloadOutlined />}
                      onClick={() =>
                        handleGuardrailQuickAction(visibleRevaluationAction)
                      }
                    >
                      {visibleRevaluationAction.label}
                    </Button>
                  )}

                  {showQuantityAdjustment && (
                    <Button
                      icon={<CalculatorOutlined />}
                      onClick={() => setCapacityCalculatorVisible(true)}
                    >
                      Ajuster la quantité
                    </Button>
                  )}

                  {canEditSelectedRecipe && (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() =>
                        handleGuardrailQuickAction({ type: "edit_recipe" })
                      }
                    >
                      Modifier la {productionTerminology.configurationSingular}
                    </Button>
                  )}
                </div>
              )}

              {calculationResults.outputs?.length > 0 && (
                <div className="production-process-desktop__output">
                  <div className="production-process-desktop__output-title">
                    <ExperimentOutlined />
                    {calculationResults.outputs.length === 1
                      ? productionTerminology.costContext === "cuisine"
                        ? "Plat obtenu"
                        : "Produit obtenu"
                      : productionTerminology.outputProductsObtained}
                  </div>
                  {calculationResults.outputs.map((output, index) => (
                    <div
                      className="production-process-desktop__output-row"
                      key={
                        output.id ||
                        output.product_id ||
                        `${output.product_name}-${index}`
                      }
                    >
                      <div className="production-process-desktop__output-name">
                        <Avatar
                          src={output.image}
                          icon={!output.image ? <RestOutlined /> : undefined}
                          size={44}
                        />
                        <span>{output.product_name}</span>
                      </div>
                      <strong>
                        {formatQuantity(
                          output.result_quantity ?? output.quantity ?? 0
                        )}{" "}
                        {output.unit_short_name || ""}
                      </strong>
                    </div>
                  ))}
                </div>
              )}

              <div className="production-process-desktop__launch">
                <Button
                  type="primary"
                  icon={<ExperimentOutlined />}
                  size="large"
                  block
                  onClick={handleProduce}
                  disabled={!canLaunchNow}
                  loading={producing}
                >
                  {productionTerminology.launchAction}
                </Button>
                {(launchDisabledReason ||
                  pricingGuardrails?.requires_acknowledgement) && (
                  <span className="production-process-desktop__launch-reason">
                    {launchDisabledReason ||
                      "Une confirmation sera demandée avant le lancement."}
                  </span>
                )}
              </div>
            </div>
          </aside>
          </>
        )}
      </div>

      <ProductionCapacityCalculator
        open={capacityCalculatorVisible}
        onCancel={() => setCapacityCalculatorVisible(false)}
        onApply={handleApplyCapacity}
        unit={selectedUnit}
        companyId={selectedCompany}
        warehouseId={selectedWarehouse}
        terminology={productionTerminology}
      />

      <StockShortageActionsModal
        open={stockShortageActionsVisible}
        title={`Approvisionner les ${productionTerminology.materialPlural} manquants`}
        items={buildMissingStockItems(calculationResults?.materials)}
        canPurchase={canCreatePurchase}
        canAdjust={canCreateStockAdjustment}
        onCancel={() => setStockShortageActionsVisible(false)}
        onPurchase={() => handleStockShortageResolution("purchase")}
        onAdjustment={() => handleStockShortageResolution("adjustment")}
      />

      <Modal
        title="Revaloriser un lot"
        open={lotResolutionModalVisible}
        onCancel={() => handleCloseLotResolutionModal()}
        onOk={() => lotResolutionForm.submit()}
        confirmLoading={lotResolutionSubmitting}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        {selectedQuickAction && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Le lot ${selectedQuickAction.lot_code || ""} de ${
                selectedQuickAction.product_name ||
                productionTerminology.materialDemonstrativeSingular
              } sera revalorise.`}
              description={`Cette action conserve le stock physique et met a jour uniquement le cout unitaire du lot pour les prochains calculs FIFO. Cout actuel: ${formatCurrency(
                selectedQuickAction.current_unit_cost
              )}${
                selectedQuickAction.unit_short_name
                  ? ` / ${selectedQuickAction.unit_short_name}`
                  : ""
              }. Prix habituel de reference: ${formatCurrency(
                selectedQuickAction.purchase_price_reference
              )}${
                selectedQuickAction.unit_short_name
                  ? ` / ${selectedQuickAction.unit_short_name}`
                  : ""
              }.`}
            />

            <Form
              form={lotResolutionForm}
              layout="vertical"
              onFinish={handleSubmitLotResolution}
            >
              <Form.Item label={productionTerminology.materialSingularTitle}>
                <Text strong>{selectedQuickAction.product_name}</Text>
              </Form.Item>

              <Form.Item
                name="unit_cost"
                label={`Nouveau cout unitaire du lot${
                  selectedQuickAction.unit_short_name
                    ? ` (CFA / ${selectedQuickAction.unit_short_name})`
                    : " (CFA)"
                }`}
                rules={[
                  { required: true, message: "Veuillez saisir un cout unitaire" },
                  {
                    type: "number",
                    min: 0.01,
                    message: "Le cout unitaire doit etre positif",
                  },
                ]}
                extra="Le prix habituel actuel est affiche comme reference et peut etre repris tel quel."
              >
                <InputNumber
                  min={0.01}
                  step={0.01}
                  style={{ width: "100%" }}
                  addonAfter="CFA"
                />
              </Form.Item>

              <Space wrap style={{ marginBottom: 16 }}>
                <Tag color="blue">
                  Cout actuel: {formatCurrency(selectedQuickAction.current_unit_cost)}
                  {selectedQuickAction.unit_short_name
                    ? ` / ${selectedQuickAction.unit_short_name}`
                    : ""}
                </Tag>
                <Tag color="green">
                  Prix habituel: {formatCurrency(
                    selectedQuickAction.purchase_price_reference
                  )}
                  {selectedQuickAction.unit_short_name
                    ? ` / ${selectedQuickAction.unit_short_name}`
                    : ""}
                </Tag>
                {Number(selectedQuickAction.purchase_price_reference) > 0 && (
                  <Button
                    size="small"
                    onClick={() =>
                      lotResolutionForm.setFieldsValue({
                        unit_cost: selectedQuickAction.purchase_price_reference,
                      })
                    }
                  >
                    Utiliser le prix habituel
                  </Button>
                )}
              </Space>

              <Form.Item
                name="notes"
                label="Motif"
                rules={[
                  { required: true, message: "Veuillez indiquer un motif" },
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Ex: correction du cout historique du lot"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Modal for displaying production results */}
      {productionResultData && (
        <Modal
          title={`Résultat de la ${productionTerminology.processTitle}`}
          open={productionResultModalVisible}
          onOk={() =>
            resultSalesProductionStep?.has_next
              ? navigateToNextSalesProduction(
                  resultSalesProductionReplenishment
                )
              : resultSalesProductionReplenishment
                ? navigateToSalesAfterProduction(
                    resultSalesProductionReplenishment
                  )
                : setProductionResultModalVisible(false)
          }
          onCancel={() =>
            !resultSalesProductionReplenishment &&
            setProductionResultModalVisible(false)
          }
          closable={!resultSalesProductionReplenishment}
          maskClosable={!resultSalesProductionReplenishment}
          footer={
            resultSalesProductionReplenishment
              ? [
                  <Button
                    key="return-sale"
                    type={resultSalesProductionStep?.has_next ? "default" : "primary"}
                    onClick={() =>
                      navigateToSalesAfterProduction(
                        resultSalesProductionReplenishment
                      )
                    }
                  >
                    {resultSalesProductionReplenishment.source_type === "pos"
                      ? "Retourner au POS"
                      : "Retourner à la vente"}
                  </Button>,
                  resultSalesProductionStep?.has_next ? (
                    <Button
                      key="next-production"
                      type="primary"
                      onClick={() =>
                        navigateToNextSalesProduction(
                          resultSalesProductionReplenishment
                        )
                      }
                    >
                      Préparer le produit suivant
                    </Button>
                  ) : null,
                ].filter(Boolean)
              : [
                  restaurantMode && (
                    <Button
                      key="back"
                      onClick={() => navigate("/production/kitchen")}
                    >
                      Retour cuisine
                    </Button>
                  ),
                  <Button
                    key="ok"
                    type="primary"
                    onClick={() => setProductionResultModalVisible(false)}
                  >
                    OK
                  </Button>,
                ].filter(Boolean)
          }
          width={600} // You can adjust the width as needed
        >
          {resultSalesProductionReplenishment && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Le stock du produit fabriqué a été mis à jour"
              description={
                resultSalesProductionStep?.has_next
                  ? "Vous pouvez maintenant préparer le prochain produit manquant ou retourner au panier pour le revalider."
                  : "Tous les produits fabriqués sélectionnés ont été traités. Retournez au panier pour revalider les stocks et poursuivre la vente."
              }
            />
          )}
          {productionResultData.production_code && (
            <Descriptions
              bordered
              column={1}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Code lot">
                <Tag color="purple">{productionResultData.production_code}</Tag>
              </Descriptions.Item>
            </Descriptions>
          )}

          {Number(
            productionResultData.financial_tracking?.expenses_created_count
          ) > 0 && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${productionResultData.financial_tracking.expenses_created_count} dépense(s) non payée(s) créée(s)`}
              description="Ces charges peuvent maintenant être payées depuis le module Dépenses."
            />
          )}

          {Number(
            productionResultData.financial_tracking
              ?.pending_regularization_count
          ) > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Certaines charges restent à régulariser"
              description={
                productionResultData.financial_tracking.warning ||
                "Aucune dépense n'a été créée pour les coûts variables incomplets. Utilisez l'historique de production pour les régulariser."
              }
            />
          )}

          {productionResultData.outputs &&
            productionResultData.outputs.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{productionTerminology.outputProducts}</Title>
                {productionResultData.outputs.map((output, index) => (
                  <Descriptions
                    key={index}
                    bordered
                    column={1}
                    size="small"
                    style={{ marginBottom: 8 }}
                  >
                    <Descriptions.Item label="Produit">
                      {output.product_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={productionTerminology.outputQuantityLabel}>
                      {`${output.result_quantity ?? output.quantity ?? 0} ${
                        output.unit_short_name || ""
                      }`}
                    </Descriptions.Item>
                  </Descriptions>
                ))}
              </div>
            )}

          {productionResultData.materials &&
            productionResultData.materials.length > 0 && (
              <div>
                <Title level={5}>{`${productionTerminology.materialPluralTitle} consommés`}</Title>
                <Table
                  dataSource={productionResultData.materials}
                  columns={[
                    {
                      title: productionTerminology.materialPluralTitle,
                      dataIndex: "product_name",
                      key: "product_name",
                      render: (text, record) => (
                        <Space>
                          {record.image && (
                            <Avatar
                              src={record.image}
                              size="small"
                            />
                          )}
                          <span>{text}</span>
                        </Space>
                      ),
                    },
                    {
                      title: "Quantité Utilisée",
                      key: "required_quantity",
                      align: "right",
                      render: (_, record) =>
                        `${record.required_quantity ?? record.quantity ?? 0} ${
                          record.unit_short_name || ""
                        }`,
                    },
                    {
                      title: "Lots consommés",
                      key: "lot_allocations",
                      render: (_, record) =>
                        renderLotAllocations(
                          record?.lot_allocations || [],
                          3,
                          record?.unit_short_name
                        ),
                    },
                  ]}
                  rowKey={(record) =>
                    record.id
                      ? record.id.toString()
                      : `material-${record.product_id}`
                  }
                  pagination={false}
                  size="small"
                />
              </div>
            )}
          {productionResultData.variable_costs &&
            productionResultData.variable_costs.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Couts variables</Title>
                <Table
                  dataSource={productionResultData.variable_costs}
                  columns={variableCostColumns}
                  rowKey={(record) =>
                    record.id
                      ? record.id.toString()
                      : `${record.label}-${record.cost_per_unit}`
                  }
                  pagination={false}
                  size="small"
                />
              </div>
            )}
          {productionResultData.cost_summary && (
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginTop: 16 }}
            >
              <Descriptions.Item label={`Cout ${productionTerminology.materialPlural}`}>
                {formatCurrency(productionResultData.cost_summary.total_material_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Cout variable">
                {formatCurrency(productionResultData.cost_summary.total_variable_cost)}
              </Descriptions.Item>
              {Number(
                productionResultData.cost_summary.total_depreciation_cost
              ) > 0 && (
                <Descriptions.Item label="Amortissement materiel">
                  {formatCurrency(
                    productionResultData.cost_summary.total_depreciation_cost
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Cout total">
                {formatCurrency(productionResultData.cost_summary.total_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Cout unitaire reel">
                {formatCurrency(productionResultData.cost_summary.unit_cost_real)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ProductionProcess;
